import { Button, Grid, Input, Modal, Select, Typography } from 'antd';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ZoneDropdown from '../../../components/ZonesDropdown';
import RegionsDropdown from '../../../components/RegionsDropdown';
import { useAuth } from '../../../hooks/useAuth';
import {
	ApplicationsFilter,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { UndoOutlined } from '@ant-design/icons';
import {
	VALID_ABHYASI_ID_REGEX,
	VALID_EMAIL_REGEX,
} from '../../../shared/Constants';
import useUserSession from '../../../hooks/useUserSession';
import { OptionData } from '../../../hooks/useZonesDropdown';
import { removeEmptyFilters } from '../../../shared/Utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterFunctionariesProps {
	open: boolean;
	value: ApplicationsFilter;
	onClose: () => void;
	onFilterChange: (value: ApplicationsFilter) => void;
	resetFilterValues: boolean;
	setResetFilterValues: React.Dispatch<React.SetStateAction<boolean>>;
}

interface RegionOption {
	value: number | undefined;
	label: string | undefined;
}

export type PositionAssignmentFilters = {
	position_type_code?: string;
	gender?: string;
	ref?: string;
	srcm_group?: number | string;
	partner?: number;
	name?: string;
	email?: string;
	srcmId?: string;
	region_id?: number | string;
};

const { Option } = Select;

export const FilterFunctionaries = ({
	open,
	value: { searchByProposerSrcmId },
	onClose,
	onFilterChange,
	resetFilterValues,
	setResetFilterValues,
}: FilterFunctionariesProps) => {
	const { srcmId } = useAuth();
	const { t, i18n } = useTranslation();
	const isLengthyLanguage = ['ta', 'ma', 'te', 'ka'].includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;

	const [proposerSrcmId, setProposerSrcmId] = useState<string | undefined>(
		searchByProposerSrcmId
	);
	const [proposerSrcmIdError, setProposerSrcmIdError] = useState('');
	const [emailIdError, setEmailIdError] = useState('');
	const [region, setRegion] = useState<RegionOption | null>(null);
	const [name, setName] = useState<string | undefined>('');
	const [email, setEmail] = useState<string | undefined>('');
	const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
	const [selectedZones, setSelectedZones] = useState<OptionData[]>([]);
	const [gender, setGender] = useState('');
	const [srcmGroup, setSrcmGroup] = useState<number | undefined>(undefined);
	const { selectedRole } = useUserSession();

	const isApprover = selectedRole === UserRole.APPROVER;
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;

	const functionaryTypeDropdown = useMemo(() => {
		if (isApprover) {
			return [];
		}

		return [
			{ label: 'Care Team', value: 'CARE_TEAM' },
			{ label: 'Approver', value: 'APPROVER' },
			{ label: 'Prework Admin', value: 'PREWORK_ADMIN' },
			{ label: 'Screening Admin', value: 'SCREENING_ADMIN' },
			{ label: 'NPS Admin', value: 'NPS_ADMIN' },
			{ label: 'Prework Mentor', value: 'PREWORK_MENTOR' },
			{ label: 'Proposer', value: 'PROPOSER' },
			{ label: 'Exam Admin', value: 'EXAM_ADMIN' },
			{ label: 'SNP Admin', value: 'SNP_ADMIN' },
		];
	}, [isApprover]);

	const genderDropdownOptions = [
		{ label: 'Male', value: 'M' },
		{ label: 'Female', value: 'F' },
	];

	const onNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setName(e.target.value);
		},
		[]
	);

	const onProposerSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setProposerSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setProposerSrcmIdError('invalidAbhyasiId');
			} else {
				setProposerSrcmIdError('');
			}
		},
		[]
	);

	const onEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setEmail(e.target.value);
			if (value && !VALID_EMAIL_REGEX.test(value)) {
				setEmailIdError('invalidEmail');
			} else {
				setEmailIdError('');
			}
		},
		[]
	);

	const onRoleChange = useCallback((roleVal: string) => {
		setRoleFilter(roleVal);
	}, []);

	const handleRegionChange = useCallback((value: RegionOption) => {
		setSrcmGroup(value.value ?? 0);
		setRegion(value);
		setSelectedZones([]);
	}, []);

	const handleZoneChange = useCallback(
		(value: OptionData | OptionData[] | null | undefined) => {
			const normalizedValue = Array.isArray(value)
				? value
				: value
				? [value]
				: [];
			setSelectedZones(normalizedValue);
		},
		[]
	);

	const fireFilterChange = useCallback(() => {
		const selectedFunctionaryType = functionaryTypeDropdown.find(
			(option) => option.value === roleFilter
		);
		const zoneIds = selectedZones.map((zone) => Number(zone.value));
		const hasZones = zoneIds.length > 0;
		const filters: PositionAssignmentFilters = {
			position_type_code: selectedFunctionaryType?.value,
			gender,
			ref: proposerSrcmId,
			name,
			email,
			srcm_group: hasZones ? zoneIds.join(',') : undefined,
			region_id: hasZones ? undefined : srcmGroup,
		};

		onFilterChange(removeEmptyFilters(filters));
	}, [
		onFilterChange,
		roleFilter,
		gender,
		proposerSrcmId,
		name,
		email,
		functionaryTypeDropdown,
		selectedZones,
		srcmGroup,
	]);

	const handleFilterButtonClick = useCallback(() => {
		fireFilterChange();
		onClose();
	}, [fireFilterChange, onClose]);

	const handleResetFilters = useCallback(() => {
		setName('');
		setSrcmGroup(undefined);
		setEmail('');
		setRegion(null);
		setSelectedZones([]);
		setGender('');
		setEmailIdError('');
		setRoleFilter('');
		setProposerSrcmId(undefined);
		onFilterChange({});
		onClose();
	}, [onClose, onFilterChange]);

	useEffect(() => {
		setName('');
		setEmail('');
		setRegion(null);
		setSelectedZones([]);
		setGender('');
		setEmailIdError('');
		setRoleFilter('');
		setResetFilterValues(false);
		setProposerSrcmId(undefined);
		setSrcmGroup(undefined);
	}, [resetFilterValues, setResetFilterValues]);

	return (
		<div className="flex flex-col h-full min-h-0 p-[8px]">
			<Modal
				open={open}
				onCancel={onClose}
				footer={null}
				centered
				className={
					isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
				}
				closable={true}
				maskClosable={true}
			>
				<div className="p-4 flex flex-col gap-4">
					{/* Header */}
					<div className="text-center font-sans font-bold text-lg border-b-2 border-gray-700 pb-1">
						{t('filter')}
					</div>

					{/* Form Fields */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{/* Name */}
						<div className="flex flex-col gap-1">
							<Text className="text-xs text-gray-600">{t('name')}</Text>
							<Input
								className="w-full"
								value={name}
								allowClear
								onChange={onNameChange}
							/>
						</div>
						{/* Email */}
						<div className="flex flex-col gap-1">
							<Text className="text-xs text-gray-600">{t('email')}</Text>
							<Input
								className="w-full"
								value={email}
								allowClear
								onChange={onEmailChange}
							/>
							{emailIdError && (
								<Text className="text-xs text-red-500">{t(emailIdError)}</Text>
							)}
						</div>
						{/* SRCMID */}
						<div className="flex flex-col gap-1">
							<Text className="text-xs text-gray-600">{t('SRCMID')}</Text>
							<Input
								value={proposerSrcmId}
								onChange={onProposerSRCMIdChange}
								className="w-full"
								allowClear
							/>
							{proposerSrcmIdError && (
								<Text className="text-xs text-red-500">
									{t(proposerSrcmIdError)}
								</Text>
							)}
						</div>
						{/* Region (Admin only) */}
						{isAdmin && (
							<div className="flex flex-col gap-1">
								<Text className="text-xs text-gray-600">{t('region')}</Text>
								<RegionsDropdown
									functionarySrcmId={srcmId}
									value={region}
									onChange={handleRegionChange}
									multiple={false}
								/>
							</div>
						)}
						{/* Zone */}
						<div className="flex flex-col gap-1">
							<Text className="text-xs text-gray-600">
								{t('byResponsibleZone')}
							</Text>
							<ZoneDropdown
								functionarySrcmId={srcmId}
								value={selectedZones}
								onChange={handleZoneChange}
								multiple={true}
								regionId={region?.value}
							/>
						</div>
						{/* Gender*/}
						<div className="flex flex-col gap-1">
							<Text className="text-xs text-gray-600">{t('gender')}</Text>

							<Select
								value={gender ?? 'NOT_PROVIDED'}
								options={genderDropdownOptions}
								onChange={setGender}
							/>
							{genderDropdownOptions.map((option) => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</div>
						{/* Functionary Type */}
						{!isApprover && (
							<div className="flex flex-col gap-1">
								<Text className="text-xs text-gray-600">
									{t('functionaryType')}
								</Text>
								<Select
									showSearch
									defaultActiveFirstOption={false}
									showArrow={false}
									filterOption={false}
									onChange={onRoleChange}
									value={roleFilter}
								>
									{functionaryTypeDropdown.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</div>
						)}
					</div>

					{/* Footer Buttons */}
					<div className="flex flex-col md:flex-row gap-2 mt-4">
						<Button
							className="w-full md:flex-1 h-9 text-primary-blue font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							{t('resetFilters')}
							<UndoOutlined className="ml-2" />
						</Button>

						<Button
							className={`w-full md:flex-1 h-9 text-primary-blue font-sans font-bold ${
								isStackVertical ? 'p-2' : ''
							}`}
							onClick={onClose}
						>
							{t('closeFilter')}
						</Button>

						<Button
							type="primary"
							onClick={handleFilterButtonClick}
							className={`w-full md:flex-1 h-9 font-sans font-bold ${
								isStackVertical ? 'p-2 mt-3' : ''
							}`}
							disabled={proposerSrcmIdError || emailIdError ? true : false}
						>
							{t('applyFilter')}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};
